// See the drawing or model (T5): the 2D and 3D Detail Drawings, the model,
// and the datasheet for a bought part, from Engineering's own copies. The
// revision is always shown, and a caution when Engineering has changed the
// assembly since the release being built.
import { Suspense, lazy, useState } from 'react';

import type { DocumentResponse } from '../../api/generated/DocumentResponse';
import { useDocuments } from '../../api/queries';
import { Badge, Button, Dialog, EmptyState, ErrorPanel, Loading } from '../../design/components';
import { formatDateTime } from '../../design/format';

const PdfViewer = lazy(() => import('./PdfViewer'));
const ModelViewer = lazy(() => import('./ModelViewer'));

type Kind = 'pdf' | 'model' | 'image' | 'file';

export function kindOf(document: DocumentResponse): Kind {
    const type = document.contentType.toLowerCase();
    const name = document.fileName.toLowerCase();
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    if (type.startsWith('model/gltf') || name.endsWith('.glb') || name.endsWith('.gltf')) return 'model';
    if (type.startsWith('image/')) return 'image';
    return 'file';
}

const ORDER = ['2D Detail Drawing', '3D Detail Drawing', 'GLB', 'Datasheet'];

function sortDocuments(documents: readonly DocumentResponse[]): DocumentResponse[] {
    const rank = (document: DocumentResponse) => {
        const index = ORDER.indexOf(document.category ?? '');
        return index === -1 ? ORDER.length : index;
    };
    return [...documents].sort(
        (left, right) => rank(left) - rank(right) || left.fileName.localeCompare(right.fileName),
    );
}

export function DocumentsDrawer({
    itemId,
    buildId,
    onClose,
}: {
    itemId: string;
    buildId?: string;
    onClose: () => void;
}) {
    const documents = useDocuments(itemId, buildId);
    const [open, setOpen] = useState<DocumentResponse | null>(null);
    const item = documents.data;
    return (
        <Dialog open title={item ? `${item.partNumber} ${item.name ?? ''}` : 'Drawings'} onClose={onClose}>
            {documents.isPending ? <Loading what="the drawings" /> : null}
            {documents.isError ? (
                <ErrorPanel error={documents.error} title="Engineering’s drawings couldn’t be loaded." />
            ) : null}
            {item ? (
                <div className="sf-stack">
                    <div className="sf-row">
                        <Badge tone="neutral">Revision {item.revision ?? '—'}</Badge>
                        {item.attributes.map((attribute) => (
                            <Badge key={attribute.name}>
                                {attribute.name}: {attribute.value}
                            </Badge>
                        ))}
                    </div>
                    {item.caution ? (
                        <div className="sf-note sf-note--pending" role="note">
                            Engineering has changed this assembly since the release being built
                            {item.caution.releasedAt ? ` (released ${formatDateTime(item.caution.releasedAt)})` : ''}.
                            {item.caution.newerRelease ? ' A newer release exists.' : ''}
                            {item.caution.changes.length > 0 ? (
                                <ul>
                                    {item.caution.changes.map((change) => (
                                        <li key={`${change.partNumber}-${change.kind}`}>
                                            {change.partNumber}: {change.kind}
                                            {change.before !== null || change.after !== null
                                                ? ` (${String(change.before ?? '—')} → ${String(change.after ?? '—')})`
                                                : ''}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                            <div>Build to the drawing for this release; check with Engineering if in doubt.</div>
                        </div>
                    ) : null}
                    {item.documents.length === 0 ? (
                        <EmptyState>Engineering has no documents for this part.</EmptyState>
                    ) : null}
                    <ul className="sf-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {sortDocuments(item.documents).map((document) => (
                            <li
                                key={document.documentId}
                                className="sf-row"
                                style={{ justifyContent: 'space-between' }}
                            >
                                <span>
                                    <strong>{document.category ?? document.documentType}</strong> {document.fileName}{' '}
                                    <span className="sf-muted">rev {document.revision ?? item.revision ?? '—'}</span>
                                </span>
                                <Button
                                    disabled={!document.isStored}
                                    onClick={() => {
                                        setOpen(document);
                                    }}
                                >
                                    {document.isStored ? 'Open' : 'Not synced yet'}
                                </Button>
                            </li>
                        ))}
                    </ul>
                    {open ? <Viewer document={open} /> : null}
                </div>
            ) : null}
            <div className="sf-dialog-actions">
                <Button onClick={onClose}>Close</Button>
            </div>
        </Dialog>
    );
}

function Viewer({ document }: { document: DocumentResponse }) {
    const title = `${document.category ?? document.documentType}: ${document.fileName}`;
    switch (kindOf(document)) {
        case 'pdf':
            return (
                <Suspense fallback={<Loading what="the drawing" />}>
                    <PdfViewer url={document.contentUrl} title={title} />
                </Suspense>
            );
        case 'model':
            return (
                <Suspense fallback={<Loading what="the model" />}>
                    <ModelViewer url={document.contentUrl} title={title} />
                </Suspense>
            );
        case 'image':
            return <img src={document.contentUrl} alt={title} style={{ maxWidth: '100%' }} />;
        case 'file':
            return (
                <a className="sf-button sf-button--outlined" href={document.contentUrl}>
                    Download {document.fileName}
                </a>
            );
    }
}
